import { useEffect, useState } from 'react';
import { editionInfo, navItems, siteName, tickerItems } from '@/mocks/home';
import { useCountdownTo } from '@/hooks/useCountdownTo';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const countdown = useCountdownTo(18, 0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="w-full bg-foreground-950 text-background-50 dark:text-foreground-950">
        <div className="mx-auto flex h-9 w-full max-w-[1440px] items-center gap-4 px-4 text-xs md:px-8">
          <span className="flex shrink-0 items-center gap-1.5 font-label font-medium text-primary-300">
            <i className="ri-broadcast-line text-sm" />
            {editionInfo.slot}
          </span>
          <span className="hidden shrink-0 items-center gap-1.5 text-background-300 lg:flex">
            {editionInfo.episode}
            <span className="text-background-500">·</span>
            {editionInfo.desk}
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-max animate-marquee items-center gap-8 whitespace-nowrap">
              {[...tickerItems, ...tickerItems].map((item, index) => (
                <span key={`${item}-${index}`} className="flex items-center gap-2 text-background-300">
                  <span className="inline-block h-1 w-1 rounded-full bg-primary-400" />
                  {item}
                </span>
              ))}
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-1.5 text-background-300 md:flex">
            <i className="ri-refresh-line" />
            {editionInfo.updatedAt}
          </span>
        </div>
      </div>

      <div
        className={`w-full border-b transition-colors duration-300 ${
          scrolled
            ? 'border-background-200 bg-background-50/95 backdrop-blur-md'
            : 'border-transparent bg-background-50'
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-[1440px] items-center gap-4 px-4 md:px-8">
          <a href="#top" className="flex shrink-0 items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50 dark:text-foreground-950">
              <i className="ri-flashlight-fill text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">{siteName}</span>
              <span className="mt-0.5 font-label text-[10px] tracking-[0.28em] text-foreground-600">DAILY 18:00</span>
            </span>
          </a>

          <nav className="ml-4 hidden flex-1 items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="whitespace-nowrap rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-foreground-950"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 sm:flex">
              <span className="relative flex h-2 w-2 items-center justify-center">
                <span className="absolute inline-flex h-2 w-2 animate-pulse-ring rounded-full bg-accent-400" />
                <span className="inline-flex h-2 w-2 rounded-full bg-accent-500" />
              </span>
              <span className="font-label text-xs text-foreground-600">距六点场</span>
              <span className="font-label text-sm font-bold tabular-nums text-foreground-950">{countdown.label}</span>
            </div>
            <a
              href="#reminder"
              className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 md:inline-flex"
            >
              六点提醒我
            </a>
            <button
              type="button"
              aria-label="打开导航菜单"
              onClick={() => setMenuOpen((prev) => !prev)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-3-line text-xl'} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto grid w-full max-w-[1440px] grid-cols-2 gap-2 px-4 py-4 md:px-8">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5 font-label text-sm text-foreground-800"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="#reminder"
                onClick={() => setMenuOpen(false)}
                className="col-span-2 whitespace-nowrap rounded-md bg-primary-500 px-3 py-2.5 text-center font-label text-sm font-medium text-background-50 dark:text-foreground-950"
              >
                六点提醒我
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}